// src/components/admin/UserManagement.jsx
import React, { useState } from "react";
import {
  SearchIcon,
  CheckCircleIcon,
  XCircleIcon,
  UserIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/outline";
import { useAdminUsersList } from "../../hooks/user/useAdminUsersList";
import { useUpdateUserVerification } from "../../hooks/user/useUpdateUserVerification";
import Loading from "../common/Loading";
import AuditLogViewer from "./AuditLogViewer";

/**
 * Admin User Management Component
 *
 * Provides interface for admins to search users and manage verification status.
 * Requirements: 11.1, 11.2, 11.3, 11.4, 11.5
 */
const UserManagement = () => {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [verifiedFilter, setVerifiedFilter] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [verificationReason, setVerificationReason] = useState("");
  const [activeTab, setActiveTab] = useState("users");

  // Debounced search to avoid too many API calls
  const [debouncedSearch, setDebouncedSearch] = useState("");

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1); // Reset to first page on search
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  const {
    data: usersData,
    isLoading,
    error,
    refetch,
  } = useAdminUsersList({
    search: debouncedSearch,
    page,
    limit,
    sortBy,
    sortOrder,
    verifiedFilter,
  });

  const updateVerification = useUpdateUserVerification();

  const handleVerificationClick = (user, verified) => {
    setSelectedUser({ ...user, pendingVerified: verified });
    setVerificationReason(
      `Verification ${verified ? "granted" : "revoked"} by admin`
    );
  };

  const handleVerificationSubmit = async () => {
    if (!selectedUser || !verificationReason.trim()) return;

    try {
      await updateVerification.mutateAsync({
        userId: selectedUser._id,
        verified: selectedUser.pendingVerified,
        reason: verificationReason.trim(),
      });
      setSelectedUser(null);
      setVerificationReason("");
    } catch (error) {
      // Error handling is done in the hook
      // Don't close modal on error so user can retry
    }
  };

  const handleSortChange = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
    setPage(1);
  };

  const totalPages = usersData?.pagination?.totalPages || 0;
  const users = usersData?.users || [];

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <h3 className="text-lg font-bold text-red-800 mb-2">
          Error Loading Users
        </h3>
        <p className="text-red-600 mb-4">{error.message}</p>
        <button onClick={() => refetch()} className="btn btn--secondary">
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
        <div className="border-b border-gray-100 px-6">
          <nav
            className="flex gap-4"
            role="tablist"
            aria-label="User management tabs"
          >
            <button
              role="tab"
              aria-selected={activeTab === "users"}
              onClick={() => setActiveTab("users")}
              className={`py-3 px-1 border-b-2 -mb-px text-sm font-bold transition-colors ${
                activeTab === "users"
                  ? "border-deep-red text-deep-red"
                  : "border-transparent text-medium-grey hover:text-dark-grey"
              }`}
            >
              User Management
            </button>
            <button
              role="tab"
              aria-selected={activeTab === "audit"}
              onClick={() => setActiveTab("audit")}
              className={`py-3 px-1 border-b-2 -mb-px text-sm font-bold transition-colors ${
                activeTab === "audit"
                  ? "border-deep-red text-deep-red"
                  : "border-transparent text-medium-grey hover:text-dark-grey"
              }`}
            >
              Audit Log
            </button>
          </nav>
        </div>

        {/* Tab panel: Users */}
        {activeTab === "users" && (
          <div role="tabpanel" className="p-6">
            {/* Header and Search */}
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="icon-tile">
                  <UserIcon className="w-6 h-6" aria-hidden="true" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-dark-grey">
                    User Management
                  </h3>
                  <p className="text-medium-grey text-sm">
                    Search users and manage verification status
                  </p>
                </div>
              </div>

              {/* Search and Filters */}
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <SearchIcon
                    className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                    aria-hidden="true"
                  />
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by username or email..."
                    className="input pl-10 w-full"
                    aria-label="Search users"
                  />
                </div>

                <select
                  value={
                    verifiedFilter === null ? "all" : verifiedFilter.toString()
                  }
                  onChange={(e) => {
                    const value = e.target.value;
                    setVerifiedFilter(
                      value === "all" ? null : value === "true"
                    );
                    setPage(1);
                  }}
                  className="input w-full sm:w-40"
                  aria-label="Filter by verification status"
                >
                  <option value="all">All Users</option>
                  <option value="true">Verified Only</option>
                  <option value="false">Unverified Only</option>
                </select>

                <select
                  value={limit}
                  onChange={(e) => {
                    setLimit(Number(e.target.value));
                    setPage(1);
                  }}
                  className="input w-full sm:w-24"
                  aria-label="Items per page"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>

            {/* Users List */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100">
              {isLoading ? (
                <div className="p-8">
                  <Loading />
                </div>
              ) : users.length === 0 ? (
                <div className="p-8 text-center">
                  <UserIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-bold text-dark-grey mb-2">
                    No Users Found
                  </h3>
                  <p className="text-medium-grey">
                    Try adjusting your search or filters.
                  </p>
                </div>
              ) : (
                <>
                  {/* Desktop Table */}
                  <div className="hidden lg:block overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr className="text-left text-medium-grey">
                          <th className="py-3 px-4 font-medium">
                            <button
                              onClick={() => handleSortChange("username")}
                              className="flex items-center gap-1 hover:text-dark-grey"
                            >
                              Username
                              {sortBy === "username" && (
                                <span className="text-xs">
                                  {sortOrder === "asc" ? "↑" : "↓"}
                                </span>
                              )}
                            </button>
                          </th>
                          <th className="py-3 px-4 font-medium">Email</th>
                          <th className="py-3 px-4 font-medium">
                            <button
                              onClick={() => handleSortChange("verified")}
                              className="flex items-center gap-1 hover:text-dark-grey"
                            >
                              Verified
                              {sortBy === "verified" && (
                                <span className="text-xs">
                                  {sortOrder === "asc" ? "↑" : "↓"}
                                </span>
                              )}
                            </button>
                          </th>
                          <th className="py-3 px-4 font-medium">
                            <button
                              onClick={() => handleSortChange("createdAt")}
                              className="flex items-center gap-1 hover:text-dark-grey"
                            >
                              Joined
                              {sortBy === "createdAt" && (
                                <span className="text-xs">
                                  {sortOrder === "asc" ? "↑" : "↓"}
                                </span>
                              )}
                            </button>
                          </th>
                          <th className="py-3 px-4 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((user) => (
                          <tr
                            key={user._id}
                            className="border-t border-gray-100 hover:bg-gray-50"
                          >
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-3">
                                <img
                                  src={user.picture || "/default-avatar.png"}
                                  alt={user.username}
                                  className="w-8 h-8 rounded-full"
                                />
                                <div>
                                  <div className="font-medium text-dark-grey">
                                    {user.username}
                                  </div>
                                  <div className="text-xs text-medium-grey">
                                    ID: {user._id}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="py-3 px-4 text-medium-grey">
                              {user.email}
                            </td>
                            <td className="py-3 px-4">
                              {user.verified ? (
                                <span className="inline-flex items-center gap-1 text-green-600">
                                  <CheckCircleIcon className="w-4 h-4" />
                                  Verified
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-gray-500">
                                  <XCircleIcon className="w-4 h-4" />
                                  Unverified
                                </span>
                              )}
                            </td>
                            <td className="py-3 px-4 text-medium-grey">
                              {new Date(user.createdAt).toLocaleDateString()}
                            </td>
                            <td className="py-3 px-4">
                              <button
                                onClick={() =>
                                  handleVerificationClick(user, !user.verified)
                                }
                                disabled={updateVerification.isLoading}
                                className={`btn text-xs ${
                                  user.verified
                                    ? "btn--ghost text-red-600 hover:bg-red-50"
                                    : "btn--secondary"
                                }`}
                              >
                                {updateVerification.isLoading
                                  ? "Loading..."
                                  : user.verified
                                  ? "Revoke"
                                  : "Verify"}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Cards */}
                  <div className="lg:hidden p-4 space-y-3">
                    {users.map((user) => (
                      <div
                        key={user._id}
                        className="border border-gray-100 rounded-lg p-4"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <img
                              src={user.picture || "/default-avatar.png"}
                              alt={user.username}
                              className="w-10 h-10 rounded-full"
                            />
                            <div>
                              <div className="font-medium text-dark-grey">
                                {user.username}
                              </div>
                              <div className="text-xs text-medium-grey">
                                {user.email}
                              </div>
                            </div>
                          </div>
                          {user.verified ? (
                            <CheckCircleIcon className="w-5 h-5 text-green-600" />
                          ) : (
                            <XCircleIcon className="w-5 h-5 text-gray-400" />
                          )}
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="text-xs text-medium-grey">
                            Joined{" "}
                            {new Date(user.createdAt).toLocaleDateString()}
                          </div>
                          <button
                            onClick={() =>
                              handleVerificationClick(user, !user.verified)
                            }
                            disabled={updateVerification.isLoading}
                            className={`btn text-xs ${
                              user.verified
                                ? "btn--ghost text-red-600 hover:bg-red-50"
                                : "btn--secondary"
                            }`}
                          >
                            {updateVerification.isLoading
                              ? "Loading..."
                              : user.verified
                              ? "Revoke Verification"
                              : "Grant Verification"}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="border-t border-gray-100 p-4">
                      <div className="flex items-center justify-between">
                        <div className="text-sm text-medium-grey">
                          Page {page} of {totalPages}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() =>
                              setPage((prev) => Math.max(prev - 1, 1))
                            }
                            disabled={page === 1}
                            className="btn btn--ghost text-sm"
                          >
                            <ChevronLeftIcon className="w-4 h-4" />
                            Previous
                          </button>
                          <button
                            onClick={() =>
                              setPage((prev) => Math.min(prev + 1, totalPages))
                            }
                            disabled={page === totalPages}
                            className="btn btn--ghost text-sm"
                          >
                            Next
                            <ChevronRightIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Tab panel: Audit Log */}
        {activeTab === "audit" && (
          <div role="tabpanel" className="p-6">
            <AuditLogViewer />
          </div>
        )}
      </div>

      {/* Verification Reason Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-bold text-dark-grey mb-4">
              {selectedUser.pendingVerified ? "Grant" : "Revoke"} Verification
            </h3>
            <p className="text-medium-grey mb-4">
              You are about to{" "}
              {selectedUser.pendingVerified
                ? "verify"
                : "revoke verification from"}{" "}
              <strong>{selectedUser.username}</strong>. Please provide a reason
              for this action.
            </p>
            <textarea
              value={verificationReason}
              onChange={(e) => setVerificationReason(e.target.value)}
              placeholder="Enter reason for verification change..."
              className="input w-full h-24 resize-none mb-4"
            />
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setSelectedUser(null);
                  setVerificationReason("");
                }}
                className="btn btn--ghost flex-1"
              >
                Cancel
              </button>
              <button
                onClick={handleVerificationSubmit}
                disabled={
                  !verificationReason.trim() || updateVerification.isLoading
                }
                className={`btn flex-1 ${
                  selectedUser.pendingVerified
                    ? "btn--secondary"
                    : "bg-red-600 hover:bg-red-700 text-white"
                }`}
              >
                {updateVerification.isLoading
                  ? "Processing..."
                  : selectedUser.pendingVerified
                  ? "Grant"
                  : "Revoke"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
